/* Sprint 2R-2B — Customer Live Tracking Map.
 *
 * Privacy-safe live map showing ONLY the driver's current position and the
 * delivery destination. NEVER shows other stops, customers, or driver private
 * info. Used by both PublicTrackingPage (no auth) and BusinessLiveTrackingPage
 * (B2B portal — own shipments only).
 *
 * Polls /live every 25s while parcel is out_for_delivery.
 */
import React from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation, RefreshCw, AlertCircle, MapPin, Truck } from "lucide-react";

// Standard Leaflet icon fix
const driverIcon = new L.DivIcon({
  className: "ema-driver-marker",
  html: '<div style="background:#dc2626;width:34px;height:34px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-size:18px;">🚚</div>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const destIcon = new L.DivIcon({
  className: "ema-dest-marker",
  html: '<div style="background:#16a34a;width:30px;height:30px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;">📍</div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, JSON.stringify(points)]);
  return null;
}

export default function LiveTrackingMap({
  live,         // {available, lat, lng, at_iso, driver_display_name, gps_age_seconds, reason}
  destination,  // {lat, lng, label} optional — recipient destination if known to caller
  onRefresh,    // optional callback to manually refresh
  refreshing = false,
  testId = "live-tracking-map",
}) {
  // Default center — Sjælland (Copenhagen area) if nothing
  const defaultCenter = [55.6761, 12.5683];
  const points = [];
  if (live?.available && live.lat != null && live.lng != null) points.push([live.lat, live.lng]);
  if (destination?.lat != null && destination?.lng != null) points.push([destination.lat, destination.lng]);

  const center = points[0] || defaultCenter;

  return (
    <div data-testid={testId}>
      {/* Status banner */}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          {live?.available ? (
            <>
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-emerald-700 font-semibold" data-testid="live-status-on">Live position aktiv</span>
              {live.driver_display_name && (
                <span className="text-brand-text2">· Chauffør {live.driver_display_name}</span>
              )}
              {live.driver_swapped && (
                <span className="inline-block text-[10px] uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Chauffør skiftet</span>
              )}
              {live.gps_age_seconds != null && (
                <span className="text-xs text-brand-text2">· GPS for {live.gps_age_seconds}s siden</span>
              )}
            </>
          ) : (
            <>
              <AlertCircle size={14} className="text-amber-600"/>
              <span className="text-amber-700" data-testid="live-status-off">{live?.reason || "Live position er ikke tilgængelig"}</span>
            </>
          )}
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="live-refresh-btn"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""}/> Opdater
          </button>
        )}
      </div>

      <div className="border border-brand bg-white" style={{ height: 360 }}>
        <MapContainer
          center={center}
          zoom={12}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {live?.available && live.lat != null && live.lng != null && (
            <Marker position={[live.lat, live.lng]} icon={driverIcon}>
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold mb-1 flex items-center gap-1"><Truck size={14}/> Chauffør</div>
                  <div className="text-xs">{live.driver_display_name || "Undervejs"}</div>
                  {live.at_iso && (
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(live.at_iso).toLocaleTimeString("da-DK")}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          )}
          {destination?.lat != null && destination?.lng != null && (
            <Marker position={[destination.lat, destination.lng]} icon={destIcon}>
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold mb-1 flex items-center gap-1"><MapPin size={14}/> Leveringssted</div>
                  <div className="text-xs">{destination.label || "Din adresse"}</div>
                </div>
              </Popup>
            </Marker>
          )}
          <FitBounds points={points}/>
        </MapContainer>
      </div>

      {!live?.available && live?.last_seen_at_iso && (
        <div className="text-xs text-brand-text2 mt-2 flex items-center gap-1">
          <Navigation size={12}/> Sidste GPS-signal: {new Date(live.last_seen_at_iso).toLocaleString("da-DK")}
        </div>
      )}
      <div className="text-[11px] text-brand-text2 mt-3 leading-relaxed">
        Live position vises kun mens pakken er <b>ude til levering</b> og chaufføren har frisk GPS-signal.
        Vi viser ingen interne ruter, andre stop eller chaufførens private oplysninger.
      </div>
    </div>
  );
}
